import React, { createContext, useContext, useState, useMemo, useEffect } from 'react';
import {
  OPTION_ALL,
  getTypeOptionsWithCounter,
} from '../../common/filters/single-select-filter/utils';
import { ConfigurationType } from '../dependencies/types';
import { configurationTypesOptions } from './utils';
import { SearchResponse, SearchType } from './types';
import uniq from 'lodash/uniq';
import { mapMissingInternalIdToMetadataId } from '../helpers';

export interface UniversalSearchContextType {
  // State
  searchTxt: string;
  setSearchTxt: (txt: string) => void;
  isResultsLoading: boolean;
  setIsResultsLoading: (loading: boolean) => void;
  isUniversalSearchListOpen: boolean;
  setIsUniversalSearchListOpen: (open: boolean) => void;
  results?: SearchResponse;
  setResults: (results?: SearchResponse) => void;
  filterKey?: string;
  setFilterKey: (key?: string) => void;
  selectedSearchType: SearchType;
  setSelectedSearchType: (type: SearchType) => void;

  // Computed values
  filterOptions: Array<{ value: string; label: string; count?: number }>;
  searchTypeOptions: Array<{ value: SearchType; label: string }>;
  filteredResults?: SearchResponse;
  selectedFilterValue?: string;
}

const UniversalSearchContext = createContext<UniversalSearchContextType | undefined>(undefined);

export interface UniversalSearchProviderProps {
  children: React.ReactNode;
  excludeTypes?: ConfigurationType[];
}

export const UniversalSearchProvider: React.FC<UniversalSearchProviderProps> = ({
  children,
  excludeTypes = [],
}) => {
  // Simple state management
  const [searchTxt, setSearchTxt] = useState('');
  const [isResultsLoading, setIsResultsLoading] = useState(false);
  const [isUniversalSearchListOpen, setIsUniversalSearchListOpen] = useState(false);
  const [results, setResults] = useState<SearchResponse | undefined>(undefined);
  const [filterKey, setFilterKey] = useState<string | undefined>(undefined);
  const [selectedSearchType, setSelectedSearchType] = useState(SearchType.apiNameAndLabel);

  // Computed values
  const configurationOptionsValues = configurationTypesOptions.map((item) => item.value);
  const dynamicFilterOptions = Object.keys(results ?? {})
    ?.map((item) => ({
      value: item,
      label: item,
    }))
    .filter((item) => !configurationOptionsValues.includes(item.value as ConfigurationType));

  const filterOptions = useMemo(() => {
    // Filter out excluded types from configuration options
    const filteredConfigurationOptions = configurationTypesOptions.filter(
      (option) => !excludeTypes.includes(option.value),
    );

    // Filter out excluded types from dynamic options
    const filteredDynamicOptions = dynamicFilterOptions.filter(
      (option) => !excludeTypes.includes(option.value as ConfigurationType),
    );

    return [
      OPTION_ALL,
      ...getTypeOptionsWithCounter(
        uniq([...filteredConfigurationOptions, ...filteredDynamicOptions]),
        (type) => results?.[type.value as keyof SearchResponse]?.length ?? 0,
      ),
    ];
  }, [results, dynamicFilterOptions, excludeTypes]);

  const searchTypeOptions = useMemo(
    () => [
      {
        value: SearchType.apiNameAndLabel,
        label: 'Search by name',
      },
      {
        value: SearchType.content,
        label: 'Search in metadata',
      },
    ],
    [],
  );

  const filteredResults = useMemo(() => {
    let baseResults = results;

    // First, exclude any specified types from the base results
    if (baseResults && excludeTypes.length > 0) {
      baseResults = Object.fromEntries(
        Object.entries(baseResults).filter(
          ([key]) => !excludeTypes.includes(key as ConfigurationType),
        ),
      ) as SearchResponse;
    }

    // Then apply the filter key if one is selected
    if (filterKey && filterKey !== OPTION_ALL.value) {
      return baseResults && baseResults[filterKey as keyof SearchResponse]
        ? ({ [filterKey]: baseResults[filterKey as keyof SearchResponse] } as SearchResponse)
        : undefined;
    }

    //TEMPORARY: for items with missing internal id, we need to add the metadataId to the id; TODO fix this when BE ready
    if (baseResults) {
      baseResults = Object.fromEntries(
        Object.entries(baseResults).map(([key, value]) => [
          key,
          value ? value.map((item) => mapMissingInternalIdToMetadataId(item)) : [],
        ]),
      ) as SearchResponse;
    }

    return baseResults;
  }, [filterKey, results, excludeTypes]);

  // Reset filterKey if it's one of the excluded types
  useEffect(() => {
    if (filterKey && excludeTypes.includes(filterKey as ConfigurationType)) {
      setFilterKey(undefined);
    }
  }, [filterKey, excludeTypes, setFilterKey]);

  const contextValue: UniversalSearchContextType = {
    // State
    searchTxt,
    setSearchTxt,
    isResultsLoading,
    setIsResultsLoading,
    isUniversalSearchListOpen,
    setIsUniversalSearchListOpen,
    results,
    setResults,
    filterKey,
    setFilterKey,
    selectedSearchType,
    setSelectedSearchType,

    // Computed values
    filterOptions,
    searchTypeOptions,
    filteredResults,
    selectedFilterValue: filterKey,
  };

  return (
    <UniversalSearchContext.Provider value={contextValue}>
      {children}
    </UniversalSearchContext.Provider>
  );
};

export const useUniversalSearchContext = (): UniversalSearchContextType => {
  const context = useContext(UniversalSearchContext);
  if (context === undefined) {
    throw new Error('useUniversalSearchContext must be used within a UniversalSearchProvider');
  }
  return context;
};
